import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { v2Api } from './api'
import { ActivityFeed } from './ActivityFeed'
import { CommentSection } from './CommentSection'
import { Button, Textarea } from './components/Field'
import { EmptyState, ErrorBanner, LoadingPane } from './components/Feedback'
import {
  Avatar,
  DueDateChip,
  IssueKeyBadge,
  PointsBadge,
  StatusPill,
  TypeIcon,
} from './components/IssueBadges'
import { TYPE_LABELS } from './filters'
import {
  AssigneeSelect,
  CommitInput,
  DetailRow,
  LabelPicker,
  PrioritySelect,
  SprintSelect,
  StatusSelect,
  TypeSelect,
} from './IssueFields'
import { issueKeys } from './keys'
import { cn, formatMoment, relativeTime } from './lib/ui'
import type { IssueType, IssueWriteInput, TaskPriority, TaskStatus } from './types'
import { issueLabel } from './types'
import { useDeleteIssue, usePatchIssue, useSetIssueLabels } from './useIssueMutations'
import { useSprints } from './useSprints'
import { Check, Link2, TerminalSquare, X } from 'lucide-react'

import { Markdown } from './components/Markdown'
import { useCopyToClipboard } from '@/lib/hooks'
import { useLabels, useRoster, useSession } from './useWorkspace'

/**
 * The issue view: everything about one issue, used both as a slide-over from
 * the board and as a full page at its own URL.
 *
 * Every field writes through `PATCH /issues/{key}` one property at a time. The
 * server owns the consequences — a status change re-homes the card into that
 * status's default column, `done` stamps `resolved_at`, a label edit mirrors
 * the names into `tasks.tags` so the iPad still sees them — so the response is
 * adopted wholesale rather than merged with a local guess.
 */
export function IssueDetail({
  slug,
  issueRef,
  onClose,
  onOpenIssue,
  onDeleted,
  layout = 'panel',
}: {
  slug: string | null
  issueRef: string
  onClose?: () => void
  onOpenIssue?: (ref: string) => void
  onDeleted?: () => void
  layout?: 'panel' | 'page'
}) {
  const [tab, setTab] = useState<'comments' | 'history'>('comments')
  const [descriptionDraft, setDescriptionDraft] = useState<string | null>(null)
  const { copied, copy } = useCopyToClipboard()

  const session = useSession()
  const { members, canWrite } = useRoster(slug)
  const { data: labels = [] } = useLabels(slug)

  const issue = useQuery({
    queryKey: issueKeys.issue(slug ?? '', issueRef),
    queryFn: () => v2Api.getIssue(slug as string, issueRef),
    enabled: Boolean(slug && issueRef),
  })

  const projectKey = issue.data?.project?.project_key ?? issue.data?.project_id ?? null
  const { data: sprints = [] } = useSprints(slug, projectKey, false)

  const patch = usePatchIssue(slug)
  const setLabels = useSetIssueLabels(slug)
  const remove = useDeleteIssue(slug)

  // A different issue means a different draft; never carry one over.
  useEffect(() => {
    setDescriptionDraft(null)
    setTab('comments')
  }, [issueRef])

  const write = (input: IssueWriteInput) => {
    if (!canWrite) return
    patch.mutate({ ref: issueRef, input })
  }

  if (issue.isLoading) return <LoadingPane label="Loading issue" />
  if (issue.error) {
    return (
      <div className="p-4">
        <ErrorBanner error={issue.error} onRetry={() => void issue.refetch()} />
      </div>
    )
  }
  if (!issue.data) {
    return <EmptyState title="Issue not found" description="It may have been deleted or moved." />
  }

  const data = issue.data
  const done = data.status === 'done'
  const type = (data.issue_type ?? 'task') as IssueType

  return (
    <article className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-2 border-b border-ink-200 px-4 py-2.5 dark:border-ink-800">
        <TypeIcon type={type} />
        <IssueKeyBadge issueKey={issueLabel(data)} done={done} />
        <StatusPill status={data.status} />
        {data.project?.name && (
          <span className="truncate text-xs text-ink-500">in {data.project.name}</span>
        )}

        <div className="ml-auto flex items-center gap-1">
          {/* The link is the CANONICAL issue route, not whatever is in the
              address bar. Opening a card from the board leaves the URL on
              /projects/MP/board?issue=MP-2, so copying location.href would
              paste a board that happens to have a card open — and it would
              break the moment the reader's board filters differ. This pastes
              the issue itself. */}
          <button
            type="button"
            onClick={() => {
              // BASE_URL is Vite's configured base ('/app/'), so the prefix is
              // not hardcoded here and follows the build.
              const base = import.meta.env.BASE_URL.replace(/\/$/, '')
              const url = `${window.location.origin}${base}/${slug}/issues/${issueLabel(data)}`
              void copy(url, 'issue-link')
            }}
            aria-label={`Copy link to ${issueLabel(data)}`}
            title={copied === 'issue-link' ? 'Link copied' : 'Copy link to this issue'}
            className="rounded-md p-1.5 text-ink-400 transition hover:bg-ink-100 hover:text-ink-700 dark:hover:bg-ink-800 dark:hover:text-ink-200"
          >
            {copied === 'issue-link' ? (
              <Check className="size-4 text-green-600 dark:text-green-400" aria-hidden="true" />
            ) : (
              <Link2 className="size-4" aria-hidden="true" />
            )}
          </button>
          {/* The command an agent can actually act on.
              It carries the task's UUID, not the MP-9 key, because the MCP
              server has no notion of issue keys: every tool takes task_id and
              no response mentions a key, so "work on MP-9" would leave the
              agent with nothing to match. The key is still in the text for the
              human who pastes it. */}
          <button
            type="button"
            onClick={() => {
              const command =
                `claude "Work on Taskly issue ${issueLabel(data)} — task_id ${data.id}. ` +
                `Read it with the taskly MCP tools, implement it, then mark it done."`
              void copy(command, 'issue-command')
            }}
            aria-label={`Copy an agent command for ${issueLabel(data)}`}
            title={
              copied === 'issue-command'
                ? 'Command copied'
                : 'Copy a command that puts a coding agent on this issue'
            }
            className="rounded-md p-1.5 text-ink-400 transition hover:bg-ink-100 hover:text-ink-700 dark:hover:bg-ink-800 dark:hover:text-ink-200"
          >
            {copied === 'issue-command' ? (
              <Check className="size-4 text-green-600 dark:text-green-400" aria-hidden="true" />
            ) : (
              <TerminalSquare className="size-4" aria-hidden="true" />
            )}
          </button>
          {canWrite && (
            <Button
              size="sm"
              variant="danger"
              disabled={remove.isPending}
              onClick={() => {
                if (!window.confirm(`Delete ${issueLabel(data)}? This cannot be undone.`)) return
                remove.mutate(issueRef, { onSuccess: () => (onDeleted ?? onClose)?.() })
              }}
            >
              Delete
            </Button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close issue"
              className="rounded-md p-1 text-ink-400 transition hover:bg-ink-100 hover:text-ink-700 dark:hover:bg-ink-800 dark:hover:text-ink-200"
            >
              <X className="size-4" strokeWidth={2.25} aria-hidden="true" />
            </button>
          )}
        </div>
      </header>

      {(patch.error || setLabels.error || remove.error) && (
        <div className="px-4 pt-3">
          <ErrorBanner error={patch.error ?? setLabels.error ?? remove.error} />
        </div>
      )}

      <div
        className={cn(
          'min-h-0 flex-1 overflow-y-auto',
          layout === 'page' ? 'grid gap-6 p-5 lg:grid-cols-[minmax(0,1fr)_18rem]' : 'p-4',
        )}
      >
        <div className="min-w-0 space-y-5">
          <div>
            <CommitInput
              ariaLabel="Issue summary"
              value={data.title}
              disabled={!canWrite}
              onCommit={(next) => {
                const title = next.trim()
                if (title && title !== data.title) write({ title })
              }}
              className="!text-base !font-semibold !py-1.5"
            />
          </div>

          <section aria-label="Description" className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
              Description
            </h3>
            {descriptionDraft === null ? (
              <div
                role={canWrite ? 'button' : undefined}
                tabIndex={canWrite ? 0 : undefined}
                onClick={() => canWrite && setDescriptionDraft(data.description ?? '')}
                onKeyDown={(event) => {
                  if (canWrite && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault()
                    setDescriptionDraft(data.description ?? '')
                  }
                }}
                className={cn(
                  'min-h-9 rounded-md px-2 py-1.5 text-sm',
                  data.description
                    ? 'text-ink-700 dark:text-ink-200'
                    : 'text-ink-400',
                  canWrite && 'cursor-text hover:bg-ink-50 dark:hover:bg-ink-900',
                )}
              >
                {/* Rendered, not printed. whitespace-pre-wrap kept newlines but
                    showed every marker literally, so a description written with
                    "## Acceptance criteria" and "- " bullets arrived as a wall
                    of punctuation. Editing still works on the raw text: the
                    click handler above opens the textarea with data.description
                    exactly as stored, so nothing is lost in the round trip. */}
                {data.description ? (
                  <Markdown source={data.description} />
                ) : (
                  'Add a description…'
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Textarea
                  autoFocus
                  rows={6}
                  value={descriptionDraft}
                  onChange={(event) => setDescriptionDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setDescriptionDraft(null)
                  }}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => {
                      if (descriptionDraft !== data.description) {
                        write({ description: descriptionDraft })
                      }
                      setDescriptionDraft(null)
                    }}
                  >
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDescriptionDraft(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </section>

          {data.parent && (
            <section aria-label="Parent" className="space-y-1">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">Parent</h3>
              <button
                type="button"
                onClick={() => onOpenIssue?.(data.parent!.issue_key ?? data.parent!.id)}
                className="flex w-full items-center gap-2 rounded-md border border-ink-200 px-2 py-1.5 text-left text-sm transition hover:bg-ink-50 dark:border-ink-800 dark:hover:bg-ink-900"
              >
                <TypeIcon type={data.parent.issue_type} />
                <IssueKeyBadge issueKey={issueLabel(data.parent)} />
                <span className="truncate">{data.parent.title}</span>
              </button>
            </section>
          )}

          {data.subtasks.length > 0 && (
            <section aria-label="Subtasks" className="space-y-1">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
                Subtasks
                <span className="ml-1 font-normal tabular-nums">
                  ({data.subtasks.filter((entry) => entry.status === 'done').length}/
                  {data.subtasks.length})
                </span>
              </h3>
              <ul className="divide-y divide-ink-200 rounded-md border border-ink-200 dark:divide-ink-800 dark:border-ink-800">
                {data.subtasks.map((subtask) => (
                  <li key={subtask.id}>
                    <button
                      type="button"
                      onClick={() => onOpenIssue?.(subtask.issue_key ?? subtask.id)}
                      className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm transition hover:bg-ink-50 dark:hover:bg-ink-900"
                    >
                      <TypeIcon type={subtask.issue_type} />
                      <IssueKeyBadge issueKey={issueLabel(subtask)} done={subtask.status === 'done'} />
                      <span className="min-w-0 flex-1 truncate">{subtask.title}</span>
                      <StatusPill status={subtask.status} />
                      <Avatar user={subtask.assignee ?? null} size="xs" />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div>
            <div
              role="tablist"
              aria-label="Issue detail tabs"
              className="mb-3 flex gap-1 border-b border-ink-200 dark:border-ink-800"
            >
              {(['comments', 'history'] as const).map((value) => (
                <button
                  key={value}
                  role="tab"
                  type="button"
                  aria-selected={tab === value}
                  onClick={() => setTab(value)}
                  className={cn(
                    '-mb-px border-b-2 px-2 py-1.5 text-xs font-medium capitalize transition',
                    tab === value
                      ? 'border-brand-600 text-brand-700 dark:text-brand-300'
                      : 'border-transparent text-ink-500 hover:text-ink-800 dark:hover:text-ink-200',
                  )}
                >
                  {value === 'comments' ? `Comments (${data.comment_count})` : 'History'}
                </button>
              ))}
            </div>

            {tab === 'comments' ? (
              <CommentSection
                slug={slug}
                issueRef={issueRef}
                members={members}
                currentUserId={session.data?.user.id ?? null}
                canWrite={canWrite}
              />
            ) : (
              <ActivityFeed slug={slug} issueRef={issueRef} />
            )}
          </div>
        </div>

        <aside
          className={cn(
            'shrink-0 space-y-0.5 text-sm',
            layout === 'panel'
              ? 'mt-6 border-t border-ink-200 pt-4 dark:border-ink-800'
              : 'rounded-card border border-ink-200 p-3 dark:border-ink-800',
          )}
        >
          <DetailRow label="Status">
            <StatusSelect
              value={data.status}
              disabled={!canWrite}
              onChange={(status: TaskStatus) => write({ status })}
            />
          </DetailRow>

          <DetailRow label="Type">
            <TypeSelect
              value={type}
              disabled={!canWrite}
              onChange={(next: IssueType) => write({ type: next })}
            />
          </DetailRow>

          <DetailRow label="Assignee">
            <AssigneeSelect
              value={data.assignee_id}
              members={members}
              disabled={!canWrite}
              onChange={(assignee_id) => write({ assignee_id })}
            />
          </DetailRow>

          <DetailRow label="Priority">
            <PrioritySelect
              value={data.priority}
              disabled={!canWrite}
              onChange={(priority: TaskPriority) => write({ priority })}
            />
          </DetailRow>

          <DetailRow label="Sprint">
            <SprintSelect
              value={data.sprint_id}
              sprints={sprints}
              disabled={!canWrite}
              onChange={(sprint_id) => write({ sprint_id })}
            />
          </DetailRow>

          <DetailRow label="Points">
            <CommitInput
              ariaLabel="Story points"
              value={data.story_points === null || data.story_points === undefined ? '' : String(data.story_points)}
              disabled={!canWrite}
              placeholder="—"
              onCommit={(next) => {
                const trimmed = next.trim()
                if (!trimmed) {
                  write({ story_points: null })
                  return
                }
                const points = Number(trimmed)
                if (Number.isFinite(points)) write({ story_points: points })
              }}
            />
          </DetailRow>

          <DetailRow label="Due date">
            <CommitInput
              ariaLabel="Due date"
              type="date"
              value={data.due_date ?? ''}
              disabled={!canWrite}
              onCommit={(next) => write({ due_date: next || null })}
            />
          </DetailRow>

          <DetailRow label="Labels">
            <LabelPicker
              applied={data.labels ?? []}
              available={labels}
              disabled={!canWrite || setLabels.isPending}
              onChange={(next) => setLabels.mutate({ ref: issueRef, labels: next })}
            />
          </DetailRow>

          <DetailRow label="Reporter">
            <span className="flex items-center gap-1.5 text-xs text-ink-600 dark:text-ink-300">
              <Avatar user={data.reporter ?? null} size="xs" />
              {data.reporter?.name ?? 'Unknown'}
            </span>
          </DetailRow>

          <div className="mt-3 space-y-1 border-t border-ink-200 pt-3 text-xs text-ink-500 dark:border-ink-800">
            <p>Created {formatMoment(data.created_at)}</p>
            <p>Updated {relativeTime(data.updated_at)}</p>
            {data.column?.name && <p>Column: {data.column.name}</p>}
            <p className="flex items-center gap-2 pt-1">
              <span>{TYPE_LABELS[type]}</span>
              <PointsBadge points={data.story_points} />
              <DueDateChip dueDate={data.due_date} done={done} />
            </p>
          </div>
        </aside>
      </div>
    </article>
  )
}
